import { debugLog } from '@sofatutor/common/test-utils';
import { AnyShape, Circle, Rectangle, Triangle, Line, Point } from '@/types/shapes';
import { Formula, FormulaType } from '@/types/formula';
import { generateFormulaId } from '@/utils/formulaUtils';

/**
 * Configuration options for the Share view functionality.
 * These options control the layout, UI visibility, and behavior of the shared view.
 */
export interface ShareViewOptions {
  /** Layout mode: 'default' for full interactive mode, 'embedded' for iframe embedding (non-interactive) */
  layout: 'default' | 'embedded';
  /** When true, show function controls (formula editor, function tools) */
  funcControls: boolean;
  /** When true, show fullscreen toggle button (does not auto-enter fullscreen) */
  fullscreen: boolean;
  /** When true, show canvas tools UI (geometry shapes, etc.) */
  tools: boolean;
  /** When true, show zoom UI controls */
  zoom: boolean;
  /** When true, show unit selector control */
  showUnits: boolean;
  /** When true, show header with app title and description */
  header: boolean;
  /** Language code (BCP-47 format, e.g. 'en', 'de', 'fr') */
  lang: string;
  /** Admin mode - enables additional features and controls */
  admin: boolean;
  /** When true, center the grid on initial load */
  centerOnLoad: boolean;
  /** When true, render only positive quadrant and lock origin to bottom-left */
  showPositiveQuadrant: boolean;
  /** Same-origin embedding: when true, generate relative URLs for embeds */
  sameOrigin: boolean;
}

/**
 * Default Share view options.
 * These are used as fallbacks when URL parameters are missing or invalid.
 */
export const defaultShareViewOptions: ShareViewOptions = {
  layout: 'default',
  funcControls: true,
  fullscreen: false,
  tools: true,
  zoom: true,
  showUnits: true,
  header: true,
  lang: 'en', // Default language - could be made configurable
  // Default admin to ON for both standalone and embedded views; URLs can override
  admin: true,
  centerOnLoad: true,
  showPositiveQuadrant: false,
  sameOrigin: true
};

/**
 * Encodes an array of shapes into a URL-friendly string
 * Format: shape1|shape2|shape3
 * Where each shape is encoded as: type,id,x,y,rotation,fill,stroke,strokeWidth,{type-specific-properties}
 */
export function encodeShapesToString(shapes: AnyShape[]): string {
  if (!shapes.length) return '';
  
  // Limit the number of shapes to encode to prevent URL from getting too long
  const MAX_SHAPES = 50;
  const shapesToEncode = shapes.length > MAX_SHAPES ? shapes.slice(0, MAX_SHAPES) : shapes;
  
  if (shapes.length > MAX_SHAPES) {
    console.warn(`Too many shapes to encode in URL (${shapes.length}). Limiting to ${MAX_SHAPES}.`);
  }
  
  return shapesToEncode.map(shape => {
    // Common properties for all shapes
    const commonProps = [
      shape.type,
      shape.id,
      shape.position.x.toFixed(1), // Reduce precision to save space
      shape.position.y.toFixed(1),
      shape.rotation.toFixed(1),
      encodeURIComponent(shape.fillColor),
      encodeURIComponent(shape.strokeColor),
      shape.opacity.toFixed(1)
    ];
    
    // Add type-specific properties
    let typeSpecificProps: string[] = [];
    
    // Declare variables outside of switch cases to avoid linter errors
    let circle: Circle;
    let rect: Rectangle;
    let triangle: Triangle;
    let line: Line;
    
    switch (shape.type) {
      case 'circle':
        circle = shape as Circle;
        typeSpecificProps = [circle.radius.toFixed(1)];
        break;
        
      case 'rectangle':
        rect = shape as Rectangle;
        typeSpecificProps = [
          rect.width.toFixed(1),
          rect.height.toFixed(1)
        ];
        break;
        
      case 'triangle':
        triangle = shape as Triangle;
        // Encode all three points
        typeSpecificProps = [
          ...triangle.points.flatMap(p => [
            p.x.toFixed(1),
            p.y.toFixed(1)
          ])
        ];
        break;
        
      case 'line':
        line = shape as Line;
        typeSpecificProps = [
          line.startPoint.x.toFixed(1),
          line.startPoint.y.toFixed(1),
          line.endPoint.x.toFixed(1),
          line.endPoint.y.toFixed(1),
          line.length.toFixed(1)
        ];
        break;
    }
    
    // Join all properties with commas
    return [...commonProps, ...typeSpecificProps].join(',');
  }).join('|');
}

/**
 * Decodes a URL-friendly string into an array of shapes
 */
export function decodeStringToShapes(encodedString: string): AnyShape[] {
  if (!encodedString) return [];
  
  try {
    const shapeStrings = encodedString.split('|');
    
    return shapeStrings.map(shapeStr => {
      const parts = shapeStr.split(',');
      
      // Extract common properties
      const type = parts[0] as AnyShape['type'];
      const id = parts[1];
      const x = parseFloat(parts[2]);
      const y = parseFloat(parts[3]);
      const rotation = parseFloat(parts[4]);
      const fillColor = decodeURIComponent(parts[5]);
      const strokeColor = decodeURIComponent(parts[6]);
      const opacity = parseFloat(parts[7]);
      
      // Common shape properties
      const commonProps = {
        id,
        type,
        position: { x, y },
        rotation,
        fillColor,
        strokeColor,
        opacity
      };
      
      // Create the specific shape type
      switch (type) {
        case 'circle':
          return {
            ...commonProps,
            type: 'circle',
            radius: parseFloat(parts[8])
          } as Circle;
          
        case 'rectangle':
          return {
            ...commonProps,
            type: 'rectangle',
            width: parseFloat(parts[8]),
            height: parseFloat(parts[9])
          } as Rectangle;
          
        case 'triangle':
          return {
            ...commonProps,
            type: 'triangle',
            points: [
              { x: parseFloat(parts[8]), y: parseFloat(parts[9]) },
              { x: parseFloat(parts[10]), y: parseFloat(parts[11]) },
              { x: parseFloat(parts[12]), y: parseFloat(parts[13]) }
            ] as [{ x: number, y: number }, { x: number, y: number }, { x: number, y: number }]
          } as Triangle;
          
        case 'line':
          return {
            ...commonProps,
            type: 'line',
            startPoint: { x: parseFloat(parts[8]), y: parseFloat(parts[9]) },
            endPoint: { x: parseFloat(parts[10]), y: parseFloat(parts[11]) },
            length: parseFloat(parts[12])
          } as Line;
          
        default:
          throw new Error(`Unknown shape type: ${type}`);
      }
    });
  } catch (error) {
    console.error('Error decoding shapes from URL:', error);
    return [];
  }
}

/**
 * Encodes a grid position into a URL-friendly string
 */
export function encodeGridPosition(position: Point): string {
  return `${position.x.toFixed(1)},${position.y.toFixed(1)}`;
}

/**
 * Decodes a URL-friendly string into a grid position
 */
export function decodeGridPosition(encodedPosition: string): Point | null {
  if (!encodedPosition) return null;
  
  try {
    const [x, y] = encodedPosition.split(',').map(parseFloat);
    return { x, y };
  } catch (error) {
    console.error('Error decoding grid position from URL:', error);
    return null;
  }
}

/**
 * Encodes an array of formulas into a URL-friendly string
 * Format: formula1|formula2|formula3
 * Where each formula is encoded as: type,id,expression,color,strokeWidth,xRangeMin,xRangeMax,samples,scaleFactor
 */
export function encodeFormulasToString(formulas: Formula[]): string {
  if (!formulas.length) return '';
  
  // Limit the number of formulas to encode to prevent URL from getting too long
  const MAX_FORMULAS = 10;
  const formulasToEncode = formulas.length > MAX_FORMULAS ? formulas.slice(0, MAX_FORMULAS) : formulas;
  
  if (formulas.length > MAX_FORMULAS) {
    console.warn(`Too many formulas to encode in URL (${formulas.length}). Limiting to ${MAX_FORMULAS}.`);
  }
  
  const encodeVariables = (vars: Formula['variables'] | undefined): string => {
    if (!vars) return '';
    const parts: string[] = [];
    for (const [name, cfg] of Object.entries(vars)) {
      if (name.length !== 1) continue;
      const seg = [
        name,
        (cfg.value ?? 1).toString(),
        (cfg.min ?? 0).toString(),
        (cfg.max ?? 10).toString(),
        cfg.step != null ? cfg.step.toString() : ''
      ].join(',');
      parts.push(seg);
    }
    const joined = parts.join(';');
    return encodeURIComponent(joined);
  };

  return formulasToEncode.map(formula => {
    // Common properties for all formulas
    const props = [
      formula.type,
      formula.id,
      encodeURIComponent(formula.expression),
      encodeURIComponent(formula.color),
      formula.strokeWidth.toFixed(1),
      formula.xRange[0].toFixed(0),
      formula.xRange[1].toFixed(0),
      formula.samples.toString(),
      formula.scaleFactor.toFixed(2)
    ];
    
    // Add type-specific properties
    if (formula.type === 'parametric' || formula.type === 'polar') {
      if (formula.tRange) {
        props.push(formula.tRange[0].toFixed(0), formula.tRange[1].toFixed(0));
      }
    }

    // Append variables (optional)
    const encodedVars = encodeVariables(formula.variables);
    if (encodedVars) {
      props.push(encodedVars);
    }
    
    // Append function signature (optional)
    if (formula.functionName || formula.independentVar) {
      const functionName = formula.functionName || 'f';
      const independentVar = formula.independentVar || 'x';
      props.push(`${encodeURIComponent(functionName)}:${encodeURIComponent(independentVar)}`);
    }
    
    // Join all properties with commas
    return props.join(',');
  }).join('|');
}

/**
 * Decodes a URL-friendly string into an array of formulas
 */
export function decodeStringToFormulas(encodedString: string): Formula[] {
  if (!encodedString) return [];
  
  try {
    const formulaStrings = encodedString.split('|');
    
    const decodeVariables = (encoded: string | undefined): Formula['variables'] | undefined => {
      if (!encoded) return undefined;
      try {
        const raw = decodeURIComponent(encoded);
        if (!raw) return undefined;
        const variables: NonNullable<Formula['variables']> = {};
        for (const token of raw.split(';')) {
          if (!token) continue;
          const parts = token.split(',');
          const [name, vStr, minStr, maxStr, stepStr] = parts;
          if (!name || name.length !== 1) continue;
          const value = parseFloat(vStr);
          const min = parseFloat(minStr);
          const max = parseFloat(maxStr);
          const step = stepStr != null && stepStr !== '' ? parseFloat(stepStr) : undefined;
          if (isNaN(value) || isNaN(min) || isNaN(max)) continue;
          variables[name] = { value, min, max, ...(step != null && !isNaN(step) ? { step } : {}) } as any;
        }
        return Object.keys(variables).length ? variables : undefined;
      } catch {
        return undefined;
      }
    };

    return formulaStrings.map(formulaStr => {
      const parts = formulaStr.split(',');
      
      // Extract common properties
      const type = parts[0] as FormulaType;
      const id = parts[1] || generateFormulaId(); // Generate a new ID if none exists
      const expression = decodeURIComponent(parts[2]);
      const color = decodeURIComponent(parts[3]);
      const strokeWidth = parseFloat(parts[4]);
      const xRangeMin = parseFloat(parts[5]);
      const xRangeMax = parseFloat(parts[6]);
      const samples = parseInt(parts[7], 10);
      const scaleFactor = parseFloat(parts[8]);
      
      // Common formula properties
      const commonProps: Formula = {
        id,
        type,
        expression,
        color,
        strokeWidth,
        xRange: [xRangeMin, xRangeMax],
        samples,
        scaleFactor
      };
      
      // Add type-specific properties + variables + function signature (optional fields)
      let currentIndex = 9; // Start after scaleFactor
      
      // Handle tRange for parametric/polar
      if (type === 'parametric' || type === 'polar') {
        if (parts.length > currentIndex + 1) {
          commonProps.tRange = [parseFloat(parts[currentIndex]), parseFloat(parts[currentIndex + 1])];
          currentIndex += 2;
        }
      }
      
      // Process remaining fields (variables and function signature)
      while (currentIndex < parts.length) {
        const field = parts[currentIndex];
        
        // Check if it's a function signature (contains colon)
        if (field.includes(':')) {
          const [encodedFunctionName, encodedIndependentVar] = field.split(':');
          try {
            commonProps.functionName = decodeURIComponent(encodedFunctionName);
            commonProps.independentVar = decodeURIComponent(encodedIndependentVar);
          } catch {
            // Ignore malformed function signature
          }
        } else {
          // Treat as variables
          const vars = decodeVariables(field);
          if (vars) {
            (commonProps as any).variables = vars;
          }
        }
        currentIndex++;
      }
      
      return commonProps;
    });
  } catch (error) {
    console.error('Error decoding formulas from URL:', error);
    return [];
  }
}

/**
 * Updates the URL with encoded shapes, formulas, and grid position without reloading the page
 */
export function updateUrlWithData(shapes: AnyShape[], formulas: Formula[], gridPosition?: Point | null): void {
  const encodedShapes = encodeShapesToString(shapes);
  const encodedFormulas = encodeFormulasToString(formulas);
  
  debugLog('Updating URL with shapes/formulas');
  
  // Create a new URL object based on the current URL
  const url = new URL(window.location.href);
  
  // Set or update the 'shapes' query parameter
  if (encodedShapes) {
    // Check if the URL would be too long
    const estimatedUrlLength = url.toString().length + encodedShapes.length + encodedFormulas.length + 20;
    if (estimatedUrlLength > 2000) {
      console.warn(`URL would be too long (${estimatedUrlLength} chars). Limiting shapes in URL.`);
      // Try with fewer shapes
      const reducedShapes = shapes.slice(0, Math.max(1, Math.floor(shapes.length / 2)));
      const reducedEncodedShapes = encodeShapesToString(reducedShapes);
      url.searchParams.set('shapes', reducedEncodedShapes);
    } else {
      url.searchParams.set('shapes', encodedShapes);
    }
  } else {
    url.searchParams.delete('shapes');
  }
  
  // Set or update the 'formulas' query parameter
  if (encodedFormulas) {
    url.searchParams.set('formulas', encodedFormulas);
  } else {
    url.searchParams.delete('formulas');
  }
  
  // Set or update the 'grid' query parameter if provided
  // Skip saving grid position if positive quadrant is enabled OR layout is embedded
  const showPositiveQuadrant = url.searchParams.get('showPositiveQuadrant') === '1';
  const layout = url.searchParams.get('layout');
  const isEmbedded = layout === 'embedded' || layout === 'noninteractive';
  if (!showPositiveQuadrant && !isEmbedded) {
    if (gridPosition) {
      const encodedGrid = encodeGridPosition(gridPosition);
      debugLog('Updating grid position in URL');
      url.searchParams.set('grid', encodedGrid);
    } else {
      // Remove the grid parameter if gridPosition is null
      url.searchParams.delete('grid');
      debugLog('Removing grid position from URL');
    }
  } else {
    // Always remove grid parameter when showPositiveQuadrant is enabled or embedded layout is active
    url.searchParams.delete('grid');
    debugLog('Removing grid position from URL - locked viewport (positiveQuadrant or embedded)');
  }
  
  // Update the URL without reloading the page
  window.history.pushState({}, '', url.toString());
  debugLog('Updated URL');
}

/**
 * Gets shapes from the URL if they exist
 */
export function getShapesFromUrl(): AnyShape[] | null {
  const url = new URL(window.location.href);
  const encodedShapes = url.searchParams.get('shapes');
  
  debugLog('Getting shapes from URL');
  
  if (!encodedShapes) return null;
  
  const shapes = decodeStringToShapes(encodedShapes);
  debugLog('Decoded shapes from URL');
  return shapes;
}

/**
 * Gets formulas from the URL if they exist
 */
export function getFormulasFromUrl(): Formula[] | null {
  const url = new URL(window.location.href);
  const encodedFormulas = url.searchParams.get('formulas');
  
  debugLog('Getting formulas from URL');
  
  if (!encodedFormulas) return null;
  
  const formulas = decodeStringToFormulas(encodedFormulas);
  debugLog('Decoded formulas from URL');
  return formulas;
}

/**
 * Gets grid position from the URL if it exists
 */
export function getGridPositionFromUrl(): Point | null {
  const url = new URL(window.location.href);
  
  // Check if showPositiveQuadrant is enabled
  const showPositiveQuadrant = url.searchParams.get('showPositiveQuadrant') === '1';
  if (showPositiveQuadrant) {
    debugLog('Skipping grid position from URL - showPositiveQuadrant is enabled');
    return null; // Don't load grid position from URL when positive quadrant is enabled
  }
  
  // Ignore grid position in embedded layout to avoid overriding embedded centering/locking
  const layout = url.searchParams.get('layout');
  const isEmbedded = layout === 'embedded' || layout === 'noninteractive';
  if (isEmbedded) {
    debugLog('Skipping grid position from URL - embedded layout');
    return null;
  }
  
  const encodedPosition = url.searchParams.get('grid');
  
  debugLog('Getting grid position from URL');
  
  if (!encodedPosition) return null;
  
  const position = decodeGridPosition(encodedPosition);
  debugLog('Decoded grid position from URL');
  return position;
}

/**
 * Parses Share view options from a URL search string.
 * Missing parameters default to values from defaultShareViewOptions.
 * Invalid language codes fall back to the default language.
 * 
 * @param search - URL search string (e.g., "?layout=noninteractive&tools=0")
 * @returns ShareViewOptions object with parsed values
 * 
 * @example
 * ```typescript
 * const options = parseShareViewOptionsFromUrl("?layout=noninteractive&tools=0&lang=de");
 * // Result: { layout: 'noninteractive', tools: false, fullscreen: false, zoom: true, unitCtl: true, lang: 'de' }
 * ```
 */
export function parseShareViewOptionsFromUrl(search: string): ShareViewOptions {
  const params = new URLSearchParams(search);
  
  // Helper function to parse boolean from URL parameter (0|1)
  const parseBooleanParam = (paramName: string, defaultValue: boolean): boolean => {
    const value = params.get(paramName);
    if (value === '1') return true;
    if (value === '0') return false;
    return defaultValue;
  };
  
  // Parse layout with validation (map legacy 'noninteractive' to 'embedded')
  const layout = params.get('layout');
  const mapped = layout === 'noninteractive' ? 'embedded' : layout;
  const validLayout = mapped === 'default' ? 'default' : 
                     mapped === 'embedded' ? 'embedded' : 
                     defaultShareViewOptions.layout;
  
  // Parse language with validation (basic check for reasonable language codes)
  const lang = params.get('lang');
  const validLang = lang && /^[a-z]{2}(-[A-Z]{2})?$/.test(lang) ? lang : defaultShareViewOptions.lang;
  
  return {
    layout: validLayout,
    funcControls: parseBooleanParam('funcControls', defaultShareViewOptions.funcControls),
    fullscreen: parseBooleanParam('fullscreen', defaultShareViewOptions.fullscreen),
    tools: parseBooleanParam('tools', defaultShareViewOptions.tools),
    zoom: parseBooleanParam('zoom', defaultShareViewOptions.zoom),
    showUnits: parseBooleanParam('showUnits', defaultShareViewOptions.showUnits),
    header: parseBooleanParam('header', defaultShareViewOptions.header),
    admin: parseBooleanParam('admin', defaultShareViewOptions.admin),
    centerOnLoad: parseBooleanParam('centerOnLoad', defaultShareViewOptions.centerOnLoad),
    showPositiveQuadrant: parseBooleanParam('showPositiveQuadrant', defaultShareViewOptions.showPositiveQuadrant),
    lang: validLang,
    sameOrigin: parseBooleanParam('sameOrigin', defaultShareViewOptions.sameOrigin)
  };
}

/**
 * Serializes Share view options to a URL query string.
 * Omits parameters that match default values to keep URLs concise.
 * Parameters are ordered consistently for deterministic output.
 * 
 * @param options - ShareViewOptions object to serialize
 * @returns Query string (without leading '?')
 * 
 * @example
 * ```typescript
 * const query = serializeShareViewOptionsToQuery({
 *   layout: 'noninteractive',
 *   tools: false,
 *   fullscreen: false,
 *   zoom: true,
 *   unitCtl: true,
 *   lang: 'de'
 * });
 * // Result: "layout=noninteractive&tools=0&lang=de"
 * ```
 */
export function serializeShareViewOptionsToQuery(options: ShareViewOptions): string {
  const params = new URLSearchParams();
  
  // Add parameters only if they differ from defaults
  // Order matters for deterministic output
  const defaults = defaultShareViewOptions;
  
  if (options.layout !== defaults.layout) {
    params.set('layout', options.layout);
  }
  
  if (options.funcControls !== defaults.funcControls) {
    params.set('funcControls', options.funcControls ? '1' : '0');
  }
  
  if (options.fullscreen !== defaults.fullscreen) {
    params.set('fullscreen', options.fullscreen ? '1' : '0');
  }
  
  if (options.tools !== defaults.tools) {
    params.set('tools', options.tools ? '1' : '0');
  }
  
  if (options.zoom !== defaults.zoom) {
    params.set('zoom', options.zoom ? '1' : '0');
  }
  
  if (options.showUnits !== defaults.showUnits) {
    params.set('showUnits', options.showUnits ? '1' : '0');
  }
  
  if (options.header !== defaults.header) {
    params.set('header', options.header ? '1' : '0');
  }
  
  if (options.admin !== defaults.admin) {
    params.set('admin', options.admin ? '1' : '0');
  }
  
  if (options.centerOnLoad !== defaults.centerOnLoad) {
    params.set('centerOnLoad', options.centerOnLoad ? '1' : '0');
  }
  if (options.showPositiveQuadrant !== defaults.showPositiveQuadrant) {
    params.set('showPositiveQuadrant', options.showPositiveQuadrant ? '1' : '0');
  }
  
  if (options.lang !== defaults.lang) {
    params.set('lang', options.lang);
  }
 
  if (options.sameOrigin !== defaults.sameOrigin) {
    params.set('sameOrigin', options.sameOrigin ? '1' : '0');
  }
  
  return params.toString();
}

/**
 * Applies precedence rules to Share view options.
 * Precedence: embedded > individual toggles
 * 
 * When layout is 'embedded':
 * - Shows only function expression, graph, and parameter sliders (read-only)
 * - All other UI controls are hidden
 *
 * @param options - Input ShareViewOptions
 * @returns ShareViewOptions with precedence rules applied
 * 
 * @example
 * ```typescript
 * const input = { layout: 'embedded', tools: true, zoom: true, ... };
 * const result = applyShareViewOptionsPrecedence(input);
 * // tools and zoom will be effectively hidden due to embedded layout
 * ```
 */
export function applyShareViewOptionsPrecedence(options: ShareViewOptions): ShareViewOptions {
  const result = { ...options };

  // Embedded mode: disable all view toggles except header (which remains configurable)
  if (result.layout === 'embedded') {
    return {
      ...result,
      funcControls: false,
      fullscreen: false,
      tools: false,
      zoom: false,
      showUnits: false
      // header remains as provided (can be toggled on/off)
    };
  }

  // No separate noninteractive mode anymore

  // Otherwise respect individual toggles as-is
  return result;
}

/**
 * Applies precedence rules to Share view options with SharePanel awareness.
 * When SharePanel is open, embedded and noninteractive modes are ignored to allow configuration.
 * 
 * @param options - Input ShareViewOptions  
 * @param isSharePanelOpen - Whether the SharePanel is currently open
 * @returns ShareViewOptions with precedence rules applied
 */
export function applyShareViewOptionsWithPanelState(
  options: ShareViewOptions, 
  _isSharePanelOpen: boolean
): ShareViewOptions {
  // Keep the current layout even when the SharePanel is open.
  // Configuration availability is handled inside the modal components.
  return applyShareViewOptionsPrecedence(options);
}

/**
 * Merges Share view options from URL search parameters with existing options.
 * URL parameters take precedence over existing options.
 * Missing URL parameters preserve existing values.
 * 
 * @param existingOptions - Current ShareViewOptions
 * @param search - URL search string
 * @returns Merged ShareViewOptions
 */
export function mergeShareViewOptionsFromUrl(
  existingOptions: ShareViewOptions,
  search: string
): ShareViewOptions {
  const urlOptions = parseShareViewOptionsFromUrl(search);
  const params = new URLSearchParams(search);
  
  // Only override values that are explicitly set in the URL
  const result = { ...existingOptions };
  
  if (params.has('layout')) result.layout = urlOptions.layout;
  if (params.has('funcControls')) result.funcControls = urlOptions.funcControls;
  if (params.has('fullscreen')) result.fullscreen = urlOptions.fullscreen;
  if (params.has('tools')) result.tools = urlOptions.tools;
  if (params.has('zoom')) result.zoom = urlOptions.zoom;
  if (params.has('showUnits')) result.showUnits = urlOptions.showUnits;
  if (params.has('header')) result.header = urlOptions.header;
  if (params.has('admin')) result.admin = urlOptions.admin;
  if (params.has('lang')) result.lang = urlOptions.lang;
  
  return result;
} 

// Legacy prototype helpers removed as they are no longer required